/**
 * organizationUnitDropdownHelper.ts
 *
 * This helper file contains utility functions related to rendering nested organization unit items
 * for the custom dropdown component used in the organization unit selection UI.
 *
 * It abstracts the recursive rendering logic used for nested structures, allowing better code reuse
 * and separation of concerns from the main component (`NestedDropdown`).
 */

import { OrganizationUnitChildItem } from './organizationUnitDropdown.type';

/**
 * Recursively renders nested dropdown items with proper indentation and styling.
 *
 * @param items - Array of nested items to render
 * @param level - Current nesting level for padding indentation (default: 0)
 * @param childSelectedLabel - Currently selected item label, used for highlighting the active item
 * @param handleSelectItem - Callback function invoked when an item is clicked
 * @returns JSX elements representing the nested structure
 */
export const renderOrganizationChildItems = (
  organizationChildItems: OrganizationUnitChildItem[],
  level = 0,
  childSelectedLabel: string,
  handleSelectItem: (item: OrganizationUnitChildItem) => void,
): JSX.Element[] => {
  return organizationChildItems.map((organizationChildItem) => (
    <div key={organizationChildItem.value}>
      <div
        className={`py-2 pr-4 cursor-pointer text-sm hover:bg-[#2367B71F] ${
          childSelectedLabel === organizationChildItem.label
            ? 'bg-[#2367B71F] font-medium'
            : ''
        }`}
        style={{ paddingLeft: `${16 + level * 16}px` }}
        onClick={() => handleSelectItem(organizationChildItem)}
      >
        {organizationChildItem.label}
        {typeof organizationChildItem?.userCount === 'number' && (
          <span className="text-gray-500 ml-2">
            ({organizationChildItem.userCount})
          </span>
        )}
      </div>
      {organizationChildItem.nestedChildItem?.length
        ? renderOrganizationChildItems(
            organizationChildItem.nestedChildItem,
            level + 1,
            childSelectedLabel,
            handleSelectItem,
          )
        : null}
    </div>
  ));
};
